// Shared runtime profile screen for KURSI LEARN
// =============================================================================
// Learner Profile — Progress Home Base
// Route: /learn/path/profile?uid=XXX
// Shows: level, XP bar, streak, completed worlds, active worlds
// =============================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Platform,
  Pressable,
  ActivityIndicator,
  Animated,
} from 'react-native';
import { router, useLocalSearchParams } from 'expo-router';
import { ArrowRight } from 'lucide-react-native';
import { supabase } from '@/utils/supabase';
import { theme } from '@/constants/theme';
import { buildRuntimeHomePath, type RuntimeBase } from '@/features/unified-learning/runtime/runtimeRoutes';
import { ProfileCard } from '@/components/learn-premium/PremiumPrimitives';
import {
  calcLevel,
  levelLabel,
  levelProgress,
  ensureKursiPlayOpenAccessSession,
} from '@/utils/kursiPlay';
import { trackKursiPlayEvent } from '@/utils/kursiPlayTelemetry';

// ── Constants ─────────────────────────────────────────────────────────────────

const BG = '#0F0F1A';
const SURFACE = '#1A1830';
const SURFACE2 = '#201E3A';
const BORDER = '#2D2B50';
const TEXT = '#F0EFF8';
const TEXT_MED = '#A09CC0';
const TEXT_DIM = '#615E80';
const PRIMARY = theme.colors.primary;
const GREEN = '#10B981';
const GOLD = '#F59E0B';
const RED = '#EF4444';

// ── Types ─────────────────────────────────────────────────────────────────────

type WorldProgress = {
  world_id: string;
  total_xp_earned: number;
  current_streak_days: number;
  longest_streak_days: number;
  completed_lessons: number;
  is_complete: boolean;
  last_activity_at: string | null;
};

type WorldMeta = {
  id: string;
  slug: string;
  world_name: string;
  description: string | null;
  primary_color: string;
  total_lessons: number;
};

type ProfileData = {
  totalXp: number;
  currentStreak: number;
  longestStreak: number;
  completedWorldsCount: number;
  activeWorlds: (WorldProgress & WorldMeta)[];
  completedWorlds: (WorldProgress & WorldMeta)[];
};

// ── Main ──────────────────────────────────────────────────────────────────────

export function RuntimeProfileScreen({ runtimeBase }: { runtimeBase: RuntimeBase }) {
  const { uid, token } = useLocalSearchParams<{ uid: string; token?: string }>();
  const [data, setData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const fadeAnim = useRef(new Animated.Value(0)).current;

  const userId = uid ?? '';
  const runtimeSession = { uid: userId, token: typeof token === 'string' ? token : undefined };
  const isGuest = !userId || userId.startsWith('guest_');

  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    setLoadError(null);
    try {
      await ensureKursiPlayOpenAccessSession(supabase);

      const { data: progressRows } = await supabase
        .from('kp_user_world_progress')
        .select('world_id, total_xp_earned, current_streak_days, longest_streak_days, completed_lessons, is_complete, last_activity_at')
        .eq('user_id', userId)
        .order('last_activity_at', { ascending: false });

      if (!progressRows || progressRows.length === 0) {
        setData({ totalXp: 0, currentStreak: 0, longestStreak: 0, completedWorldsCount: 0, activeWorlds: [], completedWorlds: [] });
        return;
      }

      const worldIds = progressRows.map((p: any) => p.world_id);

      const { data: worldRows } = await supabase
        .from('kp_worlds')
        .select('id, slug, world_name, description, primary_color')
        .in('id', worldIds);

      const { data: lessonCounts } = await supabase
        .from('kp_lessons')
        .select('world_id')
        .in('world_id', worldIds);

      const lessonCountByWorld: Record<string, number> = {};
      (lessonCounts ?? []).forEach((l: any) => {
        lessonCountByWorld[l.world_id] = (lessonCountByWorld[l.world_id] ?? 0) + 1;
      });

      const worldById: Record<string, any> = {};
      (worldRows ?? []).forEach((w: any) => { worldById[w.id] = w; });

      const combined = (progressRows as any[])
        .map((p) => {
          const w = worldById[p.world_id];
          if (!w) return null;
          return { ...p, ...w, total_lessons: lessonCountByWorld[p.world_id] ?? 0 };
        })
        .filter(Boolean) as (WorldProgress & WorldMeta)[];

      const totalXp = combined.reduce((sum, w) => sum + (w.total_xp_earned ?? 0), 0);
      const currentStreak = Math.max(0, ...combined.map((w) => w.current_streak_days ?? 0));
      const longestStreak = Math.max(0, ...combined.map((w) => w.longest_streak_days ?? 0));
      const completedWorldsCount = combined.filter((w) => w.is_complete).length;

      setData({
        totalXp,
        currentStreak,
        longestStreak,
        completedWorldsCount,
        activeWorlds: combined.filter((w) => !w.is_complete),
        completedWorlds: combined.filter((w) => w.is_complete),
      });
    } catch (e: unknown) {
      setLoadError(e instanceof Error ? e.message : 'שגיאה בטעינת הפרופיל');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!userId) return;
    void trackKursiPlayEvent('learn_profile_opened', {
      uid: userId,
      runtime_base: runtimeBase,
      is_guest: isGuest,
    });
  }, [isGuest, runtimeBase, userId]);

  useEffect(() => {
    if (!loading) {
      Animated.timing(fadeAnim, { toValue: 1, duration: 400, useNativeDriver: true }).start();
    }
  }, [loading, fadeAnim]);

  if (loading) {
    return (
      <View style={s.center}>
        <ActivityIndicator color={PRIMARY} size="large" />
        <Text style={s.loadingText}>טוען פרופיל...</Text>
      </View>
    );
  }

  if (loadError) {
    return (
      <View style={s.center}>
        <Text style={s.loadingText}>{loadError}</Text>
        <Pressable style={s.retryBtn} onPress={() => { setLoading(true); void load(); }} accessibilityRole="button">
          <Text style={s.retryText}>נסה שוב</Text>
        </Pressable>
        <Pressable style={s.backBtnAlt} onPress={() => router.back()} accessibilityRole="button">
          <Text style={s.backBtnAltText}>חזרה</Text>
        </Pressable>
      </View>
    );
  }

  const totalXp = data?.totalXp ?? 0;
  const lp = levelProgress(totalXp);
  const level = lp.level;
  const label = levelLabel(level);

  return (
    <View style={s.root}>
      <View style={s.topBar}>
        <Pressable onPress={() => router.back()} style={s.backIcon} accessibilityRole="button">
          <ArrowRight color={TEXT_MED} size={20} />
        </Pressable>
        <Text style={s.topTitle}>הפרופיל שלי</Text>
      </View>

      <ScrollView style={s.scroll} contentContainerStyle={s.scrollContent} showsVerticalScrollIndicator={false}>
        <Animated.View style={{ opacity: fadeAnim }}>

          {/* Hero */}
          <View style={s.hero}>
            <View style={[s.avatarRing, { borderColor: `${PRIMARY}60` }]}>
              <View style={[s.avatarInner, { backgroundColor: `${PRIMARY}22` }]}>
                <Text style={s.avatarLevel}>{level}</Text>
              </View>
            </View>
            <Text style={s.heroXp}>{totalXp.toLocaleString()} XP</Text>
            <Text style={s.heroLabel}>{label}</Text>
          </View>
          <ProfileCard
            name={isGuest ? 'לומד אורח' : 'לומד KURSI LEARN'}
            role="זהות לומד"
            meta={`${(data?.activeWorlds ?? []).length} מסלולים פעילים · ${(data?.completedWorlds ?? []).length} הושלמו`}
          />

          {/* XP Progress bar */}
          <XpProgressBar lp={lp} />

          {/* Stats row */}
          <View style={s.statsRow}>
            <StatBox icon="▲" value={String(data?.currentStreak ?? 0)} label="רצף נוכחי" color={GOLD} />
            <StatBox icon="★" value={String(data?.longestStreak ?? 0)} label="רצף שיא" color={GOLD} />
            <StatBox icon="✓" value={String(data?.completedWorldsCount ?? 0)} label="הושלמו" color={GREEN} />
          </View>

          {/* Guest banner */}
          {isGuest && <GuestBanner />}

          {/* Active worlds */}
          {(data?.activeWorlds ?? []).length > 0 && (
            <View style={s.section}>
              <Text style={s.sectionTitle}>בלמידה עכשיו</Text>
              {data!.activeWorlds.map((w) => (
                <WorldCard key={w.world_id} world={w} runtimeBase={runtimeBase} session={runtimeSession} />
              ))}
            </View>
          )}

          {/* Completed worlds */}
          {(data?.completedWorlds ?? []).length > 0 && (
            <View style={s.section}>
              <Text style={s.sectionTitle}>הושלמו</Text>
              {data!.completedWorlds.map((w) => (
                <WorldCard key={w.world_id} world={w} complete runtimeBase={runtimeBase} session={runtimeSession} />
              ))}
            </View>
          )}

          {/* Empty state */}
          {(data?.activeWorlds ?? []).length === 0 && (data?.completedWorlds ?? []).length === 0 && (
            <View style={s.emptyState}>
              <Text style={s.emptyTitle}>עדיין לא התחלתם ללמוד</Text>
              <Text style={s.emptySub}>
                צרו מסלול למידה ראשון — מעלים חומר לימוד והמערכת בונה שיעורים עם תרגול.
              </Text>
              <Pressable
                style={s.emptyBtn}
                onPress={() => router.push('/learn/path/create' as any)}
                accessibilityRole="button"
              >
                <Text style={s.emptyBtnText}>צור מסלול ראשון →</Text>
              </Pressable>
            </View>
          )}

        </Animated.View>
      </ScrollView>
    </View>
  );
}

// ── XpProgressBar ─────────────────────────────────────────────────────────────

function XpProgressBar({ lp }: { lp: ReturnType<typeof levelProgress> }) {
  const barWidth = useRef(new Animated.Value(0)).current;
  const pct = lp.progress / 100;
  const isMaxLevel = lp.level >= 7;

  useEffect(() => {
    Animated.timing(barWidth, {
      toValue: pct,
      duration: 800,
      delay: 200,
      useNativeDriver: false,
    }).start();
  }, [pct, barWidth]);

  return (
    <View style={s.xpCard}>
      <View style={s.xpCardTop}>
        <Text style={s.xpCardLabel}>
          {isMaxLevel ? 'רמה מקסימלית' : `רמה ${lp.level} / 7`}
        </Text>
        <Text style={s.xpCardSub}>
          {isMaxLevel ? '👑 אלוף' : `${lp.xpIntoLevel} / ${lp.xpToNextLevel} XP`}
        </Text>
      </View>
      <View style={s.xpBarBg}>
        <Animated.View style={[s.xpBarFill, {
          width: barWidth.interpolate({ inputRange: [0, 1], outputRange: ['0%', '100%'] }),
        }]} />
      </View>
    </View>
  );
}

// ── WorldCard ─────────────────────────────────────────────────────────────────

function WorldCard({
  world,
  complete = false,
  runtimeBase,
  session,
}: {
  world: WorldProgress & WorldMeta;
  complete?: boolean;
  runtimeBase: RuntimeBase;
  session: { uid: string; token?: string };
}) {
  const barWidth = useRef(new Animated.Value(0)).current;
  const totalLessons = world.total_lessons || 1;
  const completedLessons = Math.min(world.completed_lessons ?? 0, totalLessons);
  const pct = completedLessons / totalLessons;

  useEffect(() => {
    Animated.timing(barWidth, {
      toValue: pct,
      duration: 600,
      delay: 300,
      useNativeDriver: false,
    }).start();
  }, [pct, barWidth]);

  const relativeDate = world.last_activity_at
    ? getRelativeDate(world.last_activity_at)
    : null;

  const color = world.primary_color || PRIMARY;

  return (
    <Pressable
      style={[s.worldCard, complete && { borderColor: `${GREEN}33` }]}
      onPress={() => router.push(buildRuntimeHomePath(runtimeBase, world.slug, session) as any)}
      accessibilityRole="button"
      accessibilityLabel={`פתח קורס ${world.world_name}`}
    >
      <View style={s.worldCardTop}>
        <View style={[s.worldColorDot, { backgroundColor: color }]} />
        <Text style={s.worldName} numberOfLines={1}>{world.world_name}</Text>
        {complete && <Text style={s.completeBadge}>הושלם</Text>}
      </View>

      <View style={s.worldProgressRow}>
        <Text style={s.worldProgressText}>{completedLessons}/{totalLessons} שיעורים</Text>
        <Text style={s.worldXp}>★ {world.total_xp_earned ?? 0} נק׳</Text>
      </View>

      <View style={s.worldBarBg}>
        <Animated.View style={[s.worldBarFill, {
          width: barWidth.interpolate({ inputRange: [0, 1], outputRange: ['0%', '100%'] }),
          backgroundColor: complete ? GREEN : color,
        }]} />
      </View>

      {relativeDate && <Text style={s.worldDate}>{relativeDate}</Text>}
    </Pressable>
  );
}

// ── StatBox ───────────────────────────────────────────────────────────────────

function StatBox({ icon, value, label, color }: { icon: string; value: string; label: string; color: string }) {
  return (
    <View style={s.statBox}>
      <Text style={s.statBoxIcon}>{icon}</Text>
      <Text style={[s.statBoxValue, { color }]}>{value}</Text>
      <Text style={s.statBoxLabel}>{label}</Text>
    </View>
  );
}

// ── GuestBanner ───────────────────────────────────────────────────────────────

function GuestBanner() {
  return (
    <View style={s.guestBanner}>
      <View style={s.guestBannerRow}>
        <Text style={s.guestBannerIcon}>⚠️</Text>
        <Text style={s.guestBannerTitle}>ההתקדמות שלך זמנית</Text>
      </View>
      <Text style={s.guestBannerBody}>
        כמשתמש אורח, ה-XP וההתקדמות שלך נשמרים רק בדפדפן הנוכחי.
        הירשם בחינם כדי לשמור הכל ולהמשיך מכל מכשיר.
      </Text>
      <Pressable
        style={s.guestBannerBtn}
        onPress={() => router.push('/lead-gate' as any)}
        accessibilityRole="button"
      >
        <Text style={s.guestBannerBtnText}>שמור את ההתקדמות שלי — חינם →</Text>
      </Pressable>
    </View>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getRelativeDate(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'היום';
  if (days === 1) return 'אתמול';
  if (days < 7) return `לפני ${days} ימים`;
  if (days < 30) return `לפני ${Math.floor(days / 7)} שבועות`;
  return `לפני ${Math.floor(days / 30)} חודשים`;
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  root:        { flex: 1, backgroundColor: BG },
  center:      { flex: 1, backgroundColor: BG, alignItems: 'center', justifyContent: 'center', gap: 12 },
  loadingText: { fontFamily: theme.fonts.regular, color: TEXT_MED, fontSize: 14 },
  retryBtn: { backgroundColor: PRIMARY, borderRadius: 10, paddingVertical: 10, paddingHorizontal: 24 },
  retryText: { color: '#fff', fontFamily: theme.fonts.bold, fontSize: 14 },
  backBtnAlt: { borderRadius: 10, paddingVertical: 8, paddingHorizontal: 20 },
  backBtnAltText: { color: TEXT_MED, fontFamily: theme.fonts.regular, fontSize: 13 },

  topBar: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    paddingTop: Platform.OS === 'web' ? 24 : 52,
    paddingBottom: 12,
    paddingHorizontal: 16,
    backgroundColor: SURFACE,
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
    gap: 12,
  },
  backIcon: { padding: 6 },
  topTitle: { flex: 1, fontFamily: theme.fonts.bold, fontSize: 18, color: TEXT, textAlign: 'right' },

  scroll:       { flex: 1 },
  scrollContent: { paddingBottom: 60 },

  hero: {
    alignItems: 'center',
    paddingTop: 40,
    paddingBottom: 28,
    gap: 8,
  },
  avatarRing: {
    width: 96,
    height: 96,
    borderRadius: 48,
    borderWidth: 3,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4,
  },
  avatarInner: {
    width: 80,
    height: 80,
    borderRadius: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarLevel: { fontFamily: theme.fonts.bold, fontSize: 32, color: PRIMARY },
  heroXp:      { fontFamily: theme.fonts.bold, fontSize: 26, color: TEXT },
  heroLabel:   { fontFamily: theme.fonts.medium, fontSize: 14, color: TEXT_MED },

  xpCard: {
    marginHorizontal: 20,
    marginBottom: 20,
    backgroundColor: SURFACE,
    borderRadius: 14,
    padding: 16,
    borderWidth: 1,
    borderColor: BORDER,
    gap: 10,
  },
  xpCardTop:  { flexDirection: 'row-reverse', alignItems: 'center', justifyContent: 'space-between' },
  xpCardLabel: { fontFamily: theme.fonts.semiBold, fontSize: 14, color: TEXT },
  xpCardSub:   { fontFamily: theme.fonts.regular, fontSize: 13, color: TEXT_MED },
  xpBarBg:    { height: 8, backgroundColor: BORDER, borderRadius: 4, overflow: 'hidden' },
  xpBarFill:  { height: 8, backgroundColor: PRIMARY, borderRadius: 4 },

  statsRow: {
    flexDirection: 'row-reverse',
    marginHorizontal: 20,
    marginBottom: 20,
    gap: 10,
  },
  statBox: {
    flex: 1,
    backgroundColor: SURFACE,
    borderRadius: 12,
    padding: 14,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: BORDER,
    gap: 4,
  },
  statBoxIcon:  { fontSize: 20 },
  statBoxValue: { fontFamily: theme.fonts.bold, fontSize: 20 },
  statBoxLabel: { fontFamily: theme.fonts.regular, fontSize: 11, color: TEXT_MED, textAlign: 'center' },

  guestBanner: {
    marginHorizontal: 20,
    marginBottom: 20,
    backgroundColor: '#FEF3C710',
    borderWidth: 1.5,
    borderColor: '#D9770644',
    borderRadius: 14,
    padding: 16,
    gap: 10,
  },
  guestBannerRow: { flexDirection: 'row-reverse', alignItems: 'center', gap: 8 },
  guestBannerIcon: { fontSize: 18 },
  guestBannerTitle: { fontFamily: theme.fonts.bold, fontSize: 15, color: '#D97706' },
  guestBannerBody: {
    fontFamily: theme.fonts.regular,
    fontSize: 13,
    color: TEXT_MED,
    textAlign: 'right',
    lineHeight: 20,
  },
  guestBannerBtn: {
    backgroundColor: '#D97706',
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: 'center',
  },
  guestBannerBtnText: { fontFamily: theme.fonts.bold, fontSize: 14, color: '#fff' },

  section: { marginHorizontal: 20, marginBottom: 24, gap: 10 },
  sectionTitle: {
    fontFamily: theme.fonts.bold,
    fontSize: 16,
    color: TEXT,
    textAlign: 'right',
    marginBottom: 4,
  },

  worldCard: {
    backgroundColor: SURFACE,
    borderRadius: 12,
    padding: 14,
    borderWidth: 1,
    borderColor: BORDER,
    gap: 8,
  },
  worldCardTop: { flexDirection: 'row-reverse', alignItems: 'center', gap: 8 },
  worldColorDot: { width: 10, height: 10, borderRadius: 5 },
  worldName:    { flex: 1, fontFamily: theme.fonts.semiBold, fontSize: 15, color: TEXT, textAlign: 'right' },
  completeBadge: {
    fontFamily: theme.fonts.bold,
    fontSize: 11,
    color: GREEN,
    backgroundColor: `${GREEN}18`,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
  },
  worldProgressRow: { flexDirection: 'row-reverse', alignItems: 'center', justifyContent: 'space-between' },
  worldProgressText: { fontFamily: theme.fonts.regular, fontSize: 12, color: TEXT_MED },
  worldXp:          { fontFamily: theme.fonts.medium, fontSize: 12, color: TEXT_DIM },
  worldBarBg:  { height: 5, backgroundColor: BORDER, borderRadius: 3, overflow: 'hidden' },
  worldBarFill: { height: 5, borderRadius: 3 },
  worldDate:   { fontFamily: theme.fonts.regular, fontSize: 11, color: TEXT_DIM, textAlign: 'right' },

  emptyState: { alignItems: 'center', paddingVertical: 48, paddingHorizontal: 32, gap: 14 },
  emptyIcon:  { fontSize: 48 },
  emptyTitle: { fontFamily: theme.fonts.bold, fontSize: 18, color: TEXT, textAlign: 'center' },
  emptySub:   { fontFamily: theme.fonts.regular, fontSize: 14, color: TEXT_MED, textAlign: 'center', lineHeight: 22 },
  emptyBtn:   {
    backgroundColor: PRIMARY,
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 24,
    alignItems: 'center',
    marginTop: 4,
  },
  emptyBtnText: { fontFamily: theme.fonts.bold, color: '#fff', fontSize: 15 },
});
